// src/components/monthly/ReservationTable.tsx

import React from 'react';
import { useParams } from 'react-router-dom';
import { MonthlyReservationTableBody } from './ReservationTableBody';
import { ReservationImageSlider } from './ReservationImageSlider';
import { FloorPlanModal } from '../common/FloorPlanModal';
import { AmenityModal } from '../common/AmenityModal';
import { LocalizedLink } from 'src/components/common/LocalizedLink';

interface ImageData {
  src: string;
  alt: string;
  text: string;
}

interface RentalItem {
  id: number;
  room_type_id: number;
  start_date: string;
  end_date: string;
  created_at: string;
  updated_at: string | null;
}

interface ReservationTableProps {
  monthlyRate: string;
  infoText: string;
  dailyRate: string;
  studioType: string;
  isShow: boolean;
  images: ImageData[];
  roomDescription: string;
  roomTypeId: string | number | undefined;
  monthlyRentals?: RentalItem[];
  floorPlanUrl?: string;
  facility?: string[];
  facility_en?: string[];
}

export const ReservationTable: React.FC<ReservationTableProps> = ({
  monthlyRate,
  dailyRate,
  studioType,
  isShow,
  images,
  roomDescription,
  monthlyRentals = [],
  floorPlanUrl,
  facility,
  facility_en,
  roomTypeId,
}) => {
  const { propertyId } = useParams<{ propertyId?: string }>();

  // 月額料金のフォーマット
  const formattedMonthlyRate =
    monthlyRate !== 'N/A' && !isNaN(Number(monthlyRate))
      ? Math.floor(Number(monthlyRate)).toLocaleString()
      : 'N/A';

  // 日額料金のフォーマット
  const formattedDailyRate =
    dailyRate !== 'N/A' && !isNaN(Number(dailyRate))
      ? Math.floor(Number(dailyRate)).toLocaleString()
      : 'N/A';

  return (
    <div className="relative my-3">
      <div className="flex flex-col md:flex-row-reverse">
        {/* (1) 画像スライダー */}
        <div className="md:w-1/2">
          <ReservationImageSlider images={images} />
        </div>

        {/* (2) テキスト情報＋モーダルを同コンテナ内で最下部配置 */}
        <div className="md:w-1/2 flex flex-col justify-between">
          <div className="md:px-4">
            <div className="mb-1 max-md:mt-4 mr-5">
              {/* 部屋の説明 */}
              <p
                className="text-[10px] mb-2 md:mb-4"
                dangerouslySetInnerHTML={{ __html: roomDescription }}
              />
              {/* 部屋名 */}
              <p className="text-2xl">{studioType}</p>
              {/* 料金情報 */}
              <div className="flex items-baseline">
                <div className="flex items-baseline">
                  <p className="text-xl">¥{formattedMonthlyRate}</p>
                  <p className="text-xl ml-1">/ Month</p>
                </div>
                <p className="text-[9px] ml-3">(¥{formattedDailyRate}/day~)</p>
              </div>
            </div>

            {isShow && (
              <MonthlyReservationTableBody monthlyRentals={monthlyRentals} />
            )}

            {/* Apply Here ボタン */}
            <div className="flex md:mt-10 justify-center mb-8">
              <LocalizedLink
                to={`/monthly/${propertyId}/apply/${roomTypeId}`}
                className="text-white bg-black py-2 px-7 text-[15px] font-bold"
              >
                Apply Here
              </LocalizedLink>
            </div>
          </div>

          {/* (3) モーダル群: テキストエリア最下部に配置 */}
          <div className="flex text-xs max-md:text-center max-md:mt-4">
            {floorPlanUrl && <FloorPlanModal floorPlanUrl={floorPlanUrl} />}
            {facility && facility.length > 0 && (
              <AmenityModal facility={facility} facility_en={facility_en} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
